import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import logo from '../img/logo.png'; 
import Divider from '@mui/material/Divider';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AdminNotifications, markAdminNotificationsSeen } from './AdminNotifications';
import Badge from '@mui/material/Badge';  
import SchoolIcon from '@mui/icons-material/School';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CircleIcon from "@mui/icons-material/Circle";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import "../i18n";
import ArabicFlag from "../img/Arabic_flag.png";
import FrenchFlag from "../img/French_flag.png";
import EnglishFlag from "../img/English_flag.png";
import ListItemIcon from "@mui/material/ListItemIcon";

const LogoImage = styled('img')(({ theme }) => ({
  width: '150px',
  height: 'auto',
  // When screen width is less than 600px (typical phone),
  // reduce the logo size:
  [theme.breakpoints.down('sm')]: {
    width: '150px',
  },
}));

const LANGS = [
  { code: "ar", label: "العربية", flagImg: ArabicFlag },
  { code: "fr", label: "French", flagImg: FrenchFlag },
  { code: "en", label: "English", flagImg: EnglishFlag },
];

export function AdminTopBar({ marketingMode = false }) {
  const [notificationData, setNotificationData] = React.useState({
    count: 0,
    message: '',
  });
  const { i18n, t } = useTranslation();
  const [langAnchorEl, setLangAnchorEl] = React.useState(null);

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleSelectLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("darssy_lang", code);
    handleLangMenuClose();
  };


  const currentLang = LANGS.find((l) => l.code === (i18n.language || "ar")) || LANGS[0];

  const [anchorElNotification, setAnchorElNotification] = React.useState(null);
  const navigate = useNavigate();
    const [currentUser, setCurrentUser] = React.useState({
    uid: '',
    name: 'Admin',
    photoURL: '',
  });
  const [searchValue, setSearchValue] = React.useState('');

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/06071966/login');
  };

  const getInitials = (name) =>
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0])
      .join('')
      .toUpperCase();

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setCurrentUser({ uid: '', name: 'Admin', photoURL: '' });
        setNotificationData({ count: 0, message: '', url: '' });
        return;
      }

      const nameFromEmail = u.email ? u.email.split('@')[0] : '';

      try {
        // Assumes your admin doc ID == uid (matches your screenshot)
        const adminRef = doc(db, 'admin', u.uid);
        const snap = await getDoc(adminRef);

        const fullNameFromDb = snap.exists() ? snap.data()?.fullName : '';

        setCurrentUser({
          uid: u.uid,
          name: fullNameFromDb || u.displayName || nameFromEmail || 'Admin',
          photoURL: u.photoURL || '',
        });
      } catch (e) {
        console.error('Failed to load admin fullName:', e);
        setCurrentUser({
          uid: u.uid,
          name: u.displayName || nameFromEmail || 'Admin',
          photoURL: u.photoURL || '',
        });
      }
    });

    return () => unsub();
  }, []);

  React.useEffect(() => {
    const fetchComplaints = async () => {
      try {
        if (marketingMode || !currentUser.uid) return;
        const data = await AdminNotifications(currentUser.uid);
        setNotificationData(data);
      } catch (error) {
        console.error('Error fetching complaint count:', error);
      }
    };
    fetchComplaints();
  }, [currentUser.uid, i18n.language, marketingMode]);

  const handleNotificationClick = (event) => {
    setAnchorElNotification(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setAnchorElNotification(null);
  };

  const handleNotificationItemClick = async () => {
    try {
      await markAdminNotificationsSeen(currentUser.uid);
      setNotificationData((prev) => ({ ...prev, count: 0 }));
    } catch (error) {
      console.error('Error marking notifications as seen:', error);
    }

    if (notificationData.url) {
      navigate(notificationData.url);
    }
    handleNotificationClose();
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: '#ffffff',
          color: '#000000',
          borderBottom: '1px solid #e0e0e0',
        }}
      >

        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* LEFT: Logo */}
          <LogoImage src={logo} alt="Logo" />
          
          {!marketingMode && <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'right',
              px: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: '999px',
                bgcolor: '#f3f4f6',
                border: '1px solid #eef2f7',
              }}
            >
              <SearchIcon sx={{ color: '#9ca3af' }} />
              <InputBase
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t("search_placeholder")}
                sx={{
                  flex: 1,
                  fontSize: 14,
                  color: '#111827',
                  '& input::placeholder': { color: '#9ca3af', opacity: 1 },
                }}
              />
            </Box>
          </Box>}

          {/* RIGHT: User info + Bell */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Languages switch */}
            <Tooltip title={currentLang.label} arrow>
              <IconButton
                aria-label="language"
                aria-controls="language-menu-admin"
                aria-haspopup="true"
                onClick={handleLangMenuOpen}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  "&:hover": { bgcolor: "#f3f4f6" },
                  border: "1px solid #eef2f7",
                  bgcolor: "#ffffff",
                  overflow: "hidden", 
                  p: 1,               
                }}
              >
                <Box
                  component="img"
                  src={currentLang.flagImg}
                  alt={currentLang.label}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    imageRendering: "-webkit-optimize-contrast",
                  }}
                />
              </IconButton>
            </Tooltip>
            {/* Notification bell */}
            {!marketingMode && (
            <>
            <IconButton
              aria-label="notifications"
              aria-controls="notification-menu-top"
              aria-haspopup="true"
              onClick={handleNotificationClick}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "14px",
                "&:hover": { bgcolor: "#f3f4f6" },
              }}
            >
              <Badge
                badgeContent={notificationData.count}
                color="error"
                overlap="circular"
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                sx={{
                  "& .MuiBadge-badge": {
                    fontWeight: 800,
                    minWidth: 18,
                    height: 18,
                    fontSize: 11,
                    borderRadius: "999px",
                    border: "2px solid #ffffff",
                  },
                }}
              >
                <NotificationsNoneIcon sx={{ fontSize: 22, color: "#111827" }} />
              </Badge>
            </IconButton>
            </>
            )}

            <Menu
              id="language-menu-admin"
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
              onClose={handleLangMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    width: 280,
                    borderRadius: 3,
                    overflow: "hidden",
                    mt: 1,
                    boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
                    border: "1px solid rgba(0,0,0,0.06)",
                    direction: "rtl",
                  },
                },
              }}
              MenuListProps={{ sx: { p: 0 } }}
            >
              {/* Header */}
              <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                  {t("language")}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                  {t("choose_app_language")}
                </Typography>
              </Box>

              {/* Body */}
              <Box sx={{ p: 1 }}>
                {LANGS.map((lng) => {
                  const selected = (i18n.language || "ar") === lng.code;

                  return (
                    <MenuItem
                      key={lng.code}
                      onClick={() => handleSelectLanguage(lng.code)}
                      sx={{
                        borderRadius: 2,
                        alignItems: "center",
                        gap: 1.25,
                        px: 1.5,
                        py: 1.15,
                        "&:hover": { bgcolor: "#f9fafb" },
                        bgcolor: selected ? "rgba(79,70,229,0.08)" : "transparent",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <Box
                          component="img"
                          src={lng.flagImg}
                          alt={lng.label}
                          sx={{
                            width: 24,
                            height: 16,
                            borderRadius: "4px",
                            display: "block",
                            objectFit: "contain",
                            imageRendering: "-webkit-optimize-contrast",
                          }}
                        />
                      </ListItemIcon>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                          {lng.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.15 }}>
                          {lng.code.toUpperCase()}
                        </Typography>
                      </Box>

                      {selected && <CircleIcon sx={{ fontSize: 10, color: "#4f46e5" }} />}
                    </MenuItem>
                  );
                })}
              </Box>
            </Menu>

            {/* User (avatar + name) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                px: 1.5,
                py: 0.6,
                borderRadius: '999px',
                bgcolor: '#f9fafb',
                border: '1px solid #eef2f7',
              }}
            >
              <Avatar
                src={currentUser.photoURL}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: '#eef2ff',
                  color: '#4f46e5',
                  fontWeight: 700,
                }}
              >
                {!currentUser.photoURL && getInitials(currentUser.name)}
              </Avatar>

              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#111827',
                  maxWidth: 160,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {currentUser.name}
              </Typography>
            </Box>
            {marketingMode && (
              <Tooltip title={t("logout")} arrow>
                <IconButton
                  aria-label={t("logout")}
                  onClick={handleLogout}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "14px",
                    border: "1px solid #eef2f7",
                    bgcolor: "#ffffff",
                    "&:hover": { bgcolor: "#f3f4f6" },
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 21, color: "#111827" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notification popup for the top bar */}
      <Menu
        id="notification-menu-top"
        anchorEl={anchorElNotification}
        open={Boolean(anchorElNotification)}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              borderRadius: 3,
              overflow: "hidden",
              mt: 1,
              boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
              border: "1px solid rgba(0,0,0,0.06)",
              direction: "rtl",
            },
          },
        }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
            {t("notifications")}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
            {notificationData.count > 0
              ? t("you_have_new_notifications", { count: notificationData.count })
              : t("no_new_notifications")}
          </Typography>
        </Box>

        {/* Body */}
        {notificationData.count > 0 ? (
          <Box sx={{ p: 1 }}>
            <MenuItem
              onClick={handleNotificationItemClick}
              sx={{
                borderRadius: 2,
                alignItems: "flex-start",
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                "&:hover": { bgcolor: "#f9fafb" },
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "rgba(79,70,229,0.12)",
                  color: "#4f46e5",
                  flexShrink: 0,
                }}
              >
                <HeadsetMicOutlinedIcon sx={{ fontSize: 20 }} />
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                    {t("new_note")}
                  </Typography>
                  <CircleIcon sx={{ fontSize: 8, color: "#ef4444" }} />
                </Box>

                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#4b5563",
                    mt: 0.25,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.4,
                  }}
                >
                  {notificationData.message}
                </Typography>

                <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                 {t("tap_to_view_details")}
                </Typography>
              </Box>
            </MenuItem>
          </Box>
        ) : (
          <Box sx={{ p: 2.5, textAlign: "center" }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                mx: "auto",
                bgcolor: "#f3f4f6",
                color: "#6b7280",
              }}
            >
              <NotificationsNoneIcon />
            </Avatar>
            <Typography sx={{ fontSize: 14, fontWeight: 800, mt: 1, color: "#111827" }}>
             {t("no_notifications")}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.5 }}>
             {t("notifications_will_appear_here")}
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  );
}

const drawerWidth = 260;

// Common style for all sidebar items (pill + hover + active)
const navItemSx = {
  display: 'flex',
  flexDirection: 'row-reverse',        // Arabic: text then icon
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  overflow: 'hidden',
  gap: 1,
  borderRadius: '12px',
  px: 2,
  py: 1,
  color: '#4b5563',
  fontSize: 14,
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    marginLeft: 1,
    flexShrink: 0,
  },
  '&.Mui-selected': {
    bgcolor: 'rgba(79,70,229,0.10)',   // light purple background
    color: '#4f46e5',
    '& .MuiSvgIcon-root': {
      color: '#4f46e5',
    },
  },
  '&:hover': {
    bgcolor: 'rgba(79,70,229,0.05)',
  },
};

const navItemTextSx = {
  fontSize: 17,
  flex: 1,
  minWidth: 0,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  lineHeight: 1.3,
  textAlign: 'right',
};

export default function MenuAppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isActive = (path) => location.pathname === path;  // <-- AND THIS

  const handleNavigate = (path) => {
    navigate(path);
  };


  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/06071966/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };


  return (
    <Box
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        bgcolor: '#ffffff',
        borderLeft: '1px solid #e0e0e0',   // sidebar is on the RIGHT
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',      // 64px = AppBar height
        position: 'sticky',
        top: 0,
      }}
    >
      {/* Navigation items – always visible */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          px: 1.5,
        }}
      >
        {/* 1 — ALL STUDENTS */}
        <MenuItem
          selected={isActive('/06071966/AdminAllStudents')}
          onClick={() => handleNavigate('/06071966/AdminAllStudents')}
          sx={navItemSx}
        >
          <GroupAddOutlinedIcon /> 
          <Typography sx={navItemTextSx}>{t("students_page")}</Typography>
        </MenuItem>

        {/* 2 — ALL TEACHERS */}
        <MenuItem
          selected={isActive('/06071966/AdminAllTeachers')}
          onClick={() => handleNavigate('/06071966/AdminAllTeachers')}
          sx={navItemSx}
        >
          <WorkOutlineOutlinedIcon /> 
          <Typography sx={navItemTextSx}>{t("teachers_and_groups_page")}</Typography>
        </MenuItem>

        {/* 3 — ALL ADMINS */}
        <MenuItem
          selected={isActive('/06071966/AdminAllAdmins')}
          onClick={() => handleNavigate('/06071966/AdminAllAdmins')}
          sx={navItemSx}
        >
          <BadgeOutlinedIcon /> 
          <Typography sx={navItemTextSx}>{t("admin_page")}</Typography>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Remaining sidebar items */}
        <MenuItem
          selected={isActive('/06071966/billing')}
          //onClick={() => handleNavigate('/06071966/billing')}
          sx={navItemSx}
        >
          <AccountBalanceIcon />
          <Typography sx={navItemTextSx}>{t("billing_and_invoices_page")}</Typography>
        </MenuItem>

        <MenuItem
          selected={isActive('/06071966/contactUs')}
          onClick={() => handleNavigate('/06071966/contactUs')}
          sx={navItemSx}
        >
          <TextsmsOutlinedIcon />
          <Typography sx={navItemTextSx}>{t("complaints_page")}</Typography>
        </MenuItem>

        <MenuItem
          selected={location.pathname.startsWith('/06071966/blogs')}
          onClick={() => handleNavigate('/06071966/blogs')}
          sx={navItemSx}
        >
          <ArticleOutlinedIcon />
          <Typography sx={navItemTextSx}>Blog Management</Typography>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ ...navItemSx, mt: 'auto' }}
        >
          <LockOutlinedIcon />
          <Typography sx={navItemTextSx}>{t("logout")}</Typography>
        </MenuItem>
      </Box>
    </Box>
  );
}
